import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import ky from 'ky';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { Card as UiCard, Text, LinkButton } from '@imus/services-ui';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  font-family: 'Inter', sans-serif;
  background-color: var(--surface-container-high);
  height: 100vh;
  a {
    text-decoration: none;
  }
`;

const Header = styled.div`
  margin: 32px;
  display: flex;
  align-items: center;
  display: flex;
  gap: 16px;
  font-family: 'Space Grotesk', 'Inter';
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 14px;
  font-feature-settings:
    'cv11' on,
    'ss01' on;
  a:nth-of-type(1) {
    color: var(--on-surface);
  }
  a:nth-of-type(2) {
    color: var(--on-surface-var-2);
  }
  img {
    width: 24px;
    height: 24px;
  }
`;
const Content = styled.div`
  flex: 1;
  max-width: 640px;
  margin: 0 auto;
  padding: 32px;
  display: flex;
  align-self: center;
  justify-content: center;
  flex-direction: column;
`;
const ContentText = styled.div`
  text-align: center;
`;
const Card = styled(UiCard)`
  padding: 32px;
  display: flex;

  /* Inside Auto Layout */
  align-self: stretch;
  margin: 64px 0px;
`;
const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;
const CardBottom = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: auto;
`;
const ImgWrap = styled.div`
  margin-right: 32px;
  flex: 140px;
  flex-shrink: 0;
  flex-grow: 0;

  height: auto;
  img {
    width: 100%;
    border-radius: 6px;
    overflow: hidden;
  }
`;

type ReleaseData = {
  barcode: string;
  title: string;
  displayArtist: string;
  releaseAt: string | undefined;
  releasePageId: string;
  coverUrl: string;
  backUrl: string;
};

type Props = {
  logo?: React.ReactNode;
  shopName: string;
};

const RSS_ENDPOINT = process.env.NEXT_PUBLIC_RSS_ENDPOINT;

const formatDate = (d: string | undefined, locale: string) => {
  if (!d) return '';
  if (!Intl.DateTimeFormat) return d;

  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'long',
  }).format(new Date(d));
};

const getReleaseData = () => {
  const releaseJson = localStorage.getItem('release_json');
  let release: null | ReleaseData = null;
  try {
    if (releaseJson) {
      release = JSON.parse(releaseJson);
    }
  } catch (e) {
    console.error(e);
  }
  return release;
};

export function PresaveTemplate({ shopName, logo }: Props) {
  const { t, i18n } = useTranslation();
  const [release, setRelease] = useState<ReleaseData | null>(null);
  const router = useRouter();
  useEffect(() => {
    setRelease(getReleaseData());
  }, []);

  const { code } = router.query;
  useEffect(() => {
    if (code && release) {
      ky.post(RSS_ENDPOINT + '/subscribe/' + release.barcode, {
        json: {
          groupId: release.releasePageId,
          method: 'api',
          shop: shopName,
          code,
        },
      })
        .then(() => {
          window.localStorage.removeItem('release_json');
        })
        .catch(() => {
          console.error('failed code submit');
        });
    }
  }, [release, code]);
  if (!release) return null;
  return (
    <>
      <Wrapper>
        <Header>
          <a href={t('imusician-link') || ''}>iMusician</a>
          <a href={t('artist-hub-link') || ''}>{t('artist-hub')}</a>
        </Header>
        <Content>
          <ContentText>
            <Text.H2 style={{ marginBottom: '32px' }}>{t('all-good')}</Text.H2>
            <Text.BodyM>
              {t('successfully-connected-to-shop', { shopName })}
            </Text.BodyM>
          </ContentText>
          <>
            <Card>
              <ImgWrap>
                <Image
                  width={140}
                  height={140}
                  alt="release cover"
                  src={release.coverUrl}
                />
              </ImgWrap>
              <CardContent>
                <Text.H3>{release.title}</Text.H3>
                <div>
                  <Text.BodyM>{release.displayArtist}</Text.BodyM>
                </div>
                <CardBottom>
                  <div>
                    <Text.BodyS>
                      {formatDate(release.releaseAt, i18n.language)}
                    </Text.BodyS>
                  </div>
                  {logo}
                </CardBottom>
              </CardContent>
            </Card>

            <ContentText>
              <LinkButton
                href={release.backUrl}
                onClick={(e) => {
                  e.preventDefault();
                  if (window) {
                    window.close();
                  }
                }}
              >
                {t('return-to-release-page')}
              </LinkButton>
            </ContentText>
          </>
        </Content>
      </Wrapper>
    </>
  );
}
